import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { ProjectListItem, ProjectStatus } from "@/mocks/admin/projects";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreate: (project: ProjectListItem) => void;
  editProjectId?: string;
};

const moveTypes = [
  "Residential — Studio",
  "Residential — 1 BR Apartment",
  "Residential — 2 BR Apartment",
  "Residential — 2 BR Townhouse",
  "Residential — 3 BR House",
  "Residential — 4 BR House",
  "Office Relocation",
  "Commercial — Warehouse",
  "Long Distance — 3 BR House",
  "Long Distance — 4 BR House",
];

const truckOptions = [
  "16ft Cargo Van",
  "20ft Box Truck",
  "26ft Box Truck",
  "26ft + Trailer",
  "26ft + 16ft",
];

const leadOptions = [
  "Marcus Reed",
  "Samira Khan",
  "Wes Holloway",
  "Owen Patel",
  "Kenji Yamada",
  "Diego Alvarez",
  "Nina Castro",
  "Aaron Mills",
  "Tyler Brooks",
  "Ronnie Vega",
];

const avatarColors = [
  "bg-brand-500",
  "bg-indigo-500",
  "bg-pink-500",
  "bg-emerald-600",
  "bg-fuchsia-500",
  "bg-amber-600",
  "bg-rose-500",
  "bg-teal-600",
  "bg-cyan-600",
  "bg-orange-600",
  "bg-violet-500",
  "bg-lime-600",
];

function pickInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatDate(iso: string) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
}

export default function NewProjectModal({ open, onClose, onCreate, editProjectId }: Props) {
  const isEdit = !!editProjectId;
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [existingMeta, setExistingMeta] = useState<{
    initials: string;
    color: string;
    customer_id: string | null;
  } | null>(null);

  const [customer, setCustomer] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [moveType, setMoveType] = useState(moveTypes[2]);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("08:00");
  const [fromCity, setFromCity] = useState("");
  const [toCity, setToCity] = useState("");
  const [truck, setTruck] = useState(truckOptions[2]);
  const [crewCount, setCrewCount] = useState(3);
  const [leadCrew, setLeadCrew] = useState(leadOptions[0]);
  const [total, setTotal] = useState<string>("");
  const [depositPaid, setDepositPaid] = useState(false);
  const [status, setStatus] = useState<ProjectStatus>("scheduled");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      setStep(1);
      setError("");
      setSubmitting(false);
      setExistingMeta(null);

      if (isEdit && editProjectId) {
        // Try to prefill from the database row first; if not found, reset to blanks
        setLoadingExisting(true);
        supabase
          .from("moving_projects")
          .select("*")
          .eq("id", editProjectId)
          .maybeSingle()
          .then(({ data }) => {
            if (data) {
              setCustomer(data.customer_name || "");
              setPhone("");
              setEmail("");
              setMoveType(data.move_type || moveTypes[2]);
              // try to convert "May 30, 2026" to YYYY-MM-DD if possible
              const parsed = data.move_date ? new Date(data.move_date) : null;
              setDate(
                parsed && !Number.isNaN(parsed.getTime())
                  ? parsed.toISOString().slice(0, 10)
                  : "",
              );
              setTime(data.move_time || "08:00");
              setFromCity(data.from_city || "");
              setToCity(data.to_city || "");
              setTruck(data.truck || truckOptions[2]);
              setCrewCount(data.crew_count ?? 3);
              setLeadCrew(data.lead_crew || leadOptions[0]);
              setTotal(data.total != null ? String(data.total) : "");
              setDepositPaid(!!data.deposit_paid);
              setStatus((data.status as ProjectStatus) || "scheduled");
              setNotes(data.notes || "");
              setExistingMeta({
                initials: data.customer_initials || pickInitials(data.customer_name || ""),
                color: data.customer_color || avatarColors[0],
                customer_id: data.customer_id ?? null,
              });
            } else {
              // Row not in DB (e.g. mock-only project). Reset blank.
              setCustomer("");
              setPhone("");
              setEmail("");
              setMoveType(moveTypes[2]);
              setDate("");
              setTime("08:00");
              setFromCity("");
              setToCity("");
              setTruck(truckOptions[2]);
              setCrewCount(3);
              setLeadCrew(leadOptions[0]);
              setTotal("");
              setDepositPaid(false);
              setStatus("scheduled");
              setNotes("");
            }
            setLoadingExisting(false);
          });
      } else {
        setCustomer("");
        setPhone("");
        setEmail("");
        setMoveType(moveTypes[2]);
        setDate("");
        setTime("08:00");
        setFromCity("");
        setToCity("");
        setTruck(truckOptions[2]);
        setCrewCount(3);
        setLeadCrew(leadOptions[0]);
        setTotal("");
        setDepositPaid(false);
        setStatus("scheduled");
        setNotes("");
      }
    }
  }, [open, isEdit, editProjectId]);

  if (!open) return null;

  const canNext1 = customer.trim() && (isEdit || phone.trim()) && moveType;
  const canNext2 = date && time && fromCity.trim() && toCity.trim();

  const handleSubmit = async () => {
    setError("");
    const totalNum = Number(total);
    if (!total || Number.isNaN(totalNum) || totalNum < 0) {
      setError("Please enter a valid total amount.");
      return;
    }
    setSubmitting(true);

    if (isEdit && editProjectId) {
      const initials = existingMeta?.initials || pickInitials(customer);
      const color = existingMeta?.color || avatarColors[0];

      const { error: updateError } = await supabase
        .from("moving_projects")
        .update({
          customer_name: customer.trim(),
          customer_initials: initials,
          customer_color: color,
          move_type: moveType,
          move_date: formatDate(date),
          move_time: time,
          from_city: fromCity.trim(),
          to_city: toCity.trim(),
          truck,
          crew_count: crewCount,
          lead_crew: leadCrew,
          status,
          total: totalNum,
          deposit_paid: depositPaid,
          notes: notes || null,
        })
        .eq("id", editProjectId);

      if (updateError) {
        setError(updateError.message);
        setSubmitting(false);
        return;
      }

      const updated: ProjectListItem = {
        id: editProjectId,
        customer: customer.trim(),
        customerInitials: initials,
        customerColor: color,
        moveType,
        date: formatDate(date),
        time,
        fromCity: fromCity.trim(),
        toCity: toCity.trim(),
        truck,
        crewCount,
        leadCrew,
        status,
        total: totalNum,
        depositPaid,
      };

      onCreate(updated);
      setSubmitting(false);
      onClose();
      return;
    }

    // Create flow
    const newId = "MV-" + (2060 + Math.floor(Math.random() * 9000));
    const initials = pickInitials(customer);
    const color = avatarColors[Math.floor(Math.random() * avatarColors.length)];

    const { error: insertError } = await supabase.from("moving_projects").insert({
      id: newId,
      customer_name: customer.trim(),
      customer_initials: initials,
      customer_color: color,
      move_type: moveType,
      move_date: formatDate(date),
      move_time: time,
      from_city: fromCity.trim(),
      to_city: toCity.trim(),
      truck,
      crew_count: crewCount,
      lead_crew: leadCrew,
      status,
      total: totalNum,
      deposit_paid: depositPaid,
      notes: notes || null,
    });

    if (insertError) {
      setError(insertError.message);
      setSubmitting(false);
      return;
    }

    const newProject: ProjectListItem = {
      id: newId,
      customer: customer.trim(),
      customerInitials: initials,
      customerColor: color,
      moveType,
      date: formatDate(date),
      time,
      fromCity: fromCity.trim(),
      toCity: toCity.trim(),
      truck,
      crewCount,
      leadCrew,
      status,
      total: totalNum,
      depositPaid,
    };

    onCreate(newProject);
    setSubmitting(false);
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-40 bg-ink-900/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white w-full max-w-2xl rounded-lg border border-ink-100 max-h-[90vh] flex flex-col pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-ink-100 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-md bg-brand-100 text-brand-700">
                <i className={`${isEdit ? "ri-edit-line" : "ri-truck-line"} text-lg`}></i>
              </span>
              <div>
                <h2 className="font-display text-lg font-bold text-ink-900">
                  {isEdit ? `Edit Project ${editProjectId}` : "New Moving Project"}
                </h2>
                <div className="text-xs text-ink-500">
                  Step {step} of 3 ·{" "}
                  {step === 1 ? "Customer" : step === 2 ? "Move details" : "Crew & pricing"}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-cream-100 text-ink-500 cursor-pointer"
              aria-label="Close"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* Step indicator */}
          <div className="px-6 pt-4 pb-2 shrink-0">
            <div className="flex items-center gap-2">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    s <= step ? "bg-brand-500" : "bg-cream-100"
                  }`}
                ></div>
              ))}
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto scrollbar-thin px-6 py-5">
            {loadingExisting ? (
              <div className="flex items-center justify-center py-12 text-ink-400">
                <i className="ri-loader-4-line animate-spin text-2xl mr-2"></i>
                <span className="text-sm">Loading project…</span>
              </div>
            ) : (
              <>
            {step === 1 && (
              <div className="space-y-4">
                <Field label="Customer name" required>
                  <input
                    type="text"
                    value={customer}
                    onChange={(e) => setCustomer(e.target.value)}
                    placeholder="e.g. Margaret Sullivan"
                    className="form-input"
                  />
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Phone" required={!isEdit}>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="(512) 555-0142"
                      className="form-input"
                    />
                  </Field>
                  <Field label="Email">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="customer@email.com"
                      className="form-input"
                    />
                  </Field>
                </div>

                <Field label="Move type" required>
                  <select
                    value={moveType}
                    onChange={(e) => setMoveType(e.target.value)}
                    className="form-input pr-8"
                  >
                    {moveTypes.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Notes (optional)">
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value.slice(0, 500))}
                    placeholder="Anything special the crew should know — piano on 2nd floor, narrow stairs, fragile items…"
                    rows={3}
                    className="form-input resize-none"
                  />
                  <div className="text-[10px] text-ink-400 mt-1 text-right">
                    {notes.length}/500
                  </div>
                </Field>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Move date" required>
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      className="form-input"
                    />
                  </Field>
                  <Field label="Start time" required>
                    <input
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      className="form-input"
                    />
                  </Field>
                </div>

                <Field label="From (origin city)" required>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-emerald-500">
                      <i className="ri-map-pin-2-line text-sm"></i>
                    </span>
                    <input
                      type="text"
                      value={fromCity}
                      onChange={(e) => setFromCity(e.target.value)}
                      placeholder="e.g. Austin, TX"
                      className="form-input pl-9"
                    />
                  </div>
                </Field>

                <Field label="To (destination city)" required>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-rose-500">
                      <i className="ri-flag-line text-sm"></i>
                    </span>
                    <input
                      type="text"
                      value={toCity}
                      onChange={(e) => setToCity(e.target.value)}
                      placeholder="e.g. Round Rock, TX"
                      className="form-input pl-9"
                    />
                  </div>
                </Field>

                <Field label={isEdit ? "Status" : "Initial status"}>
                  <div className="flex flex-wrap gap-2">
                    {(
                      isEdit
                        ? ([
                            "scheduled",
                            "in-progress",
                            "on-hold",
                            "completed",
                            "cancelled",
                          ] as ProjectStatus[])
                        : (["scheduled", "in-progress", "on-hold"] as ProjectStatus[])
                    ).map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setStatus(s)}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full border cursor-pointer whitespace-nowrap transition-colors ${
                          status === s
                            ? "bg-ink-900 text-white border-ink-900"
                            : "bg-white text-ink-700 border-ink-200 hover:bg-cream-100"
                        }`}
                      >
                        {s.replace("-", " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                      </button>
                    ))}
                  </div>
                </Field>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <Field label="Truck">
                  <select
                    value={truck}
                    onChange={(e) => setTruck(e.target.value)}
                    className="form-input pr-8"
                  >
                    {truckOptions.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Lead mover">
                    <select
                      value={leadCrew}
                      onChange={(e) => setLeadCrew(e.target.value)}
                      className="form-input pr-8"
                    >
                      {leadOptions.map((l) => (
                        <option key={l}>{l}</option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Crew size">
                    <div className="flex items-center gap-2 bg-cream-50 border border-ink-100 rounded-md px-2 py-1.5">
                      <button
                        type="button"
                        onClick={() => setCrewCount((c) => Math.max(2, c - 1))}
                        className="w-7 h-7 flex items-center justify-center rounded bg-white border border-ink-200 text-ink-700 hover:bg-cream-100 cursor-pointer"
                      >
                        <i className="ri-subtract-line text-sm"></i>
                      </button>
                      <div className="flex-1 text-center">
                        <span className="font-display text-lg font-bold text-ink-900 tabular-nums">
                          {crewCount}
                        </span>
                        <span className="text-xs text-ink-500 ml-1">movers</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setCrewCount((c) => Math.min(8, c + 1))}
                        className="w-7 h-7 flex items-center justify-center rounded bg-white border border-ink-200 text-ink-700 hover:bg-cream-100 cursor-pointer"
                      >
                        <i className="ri-add-line text-sm"></i>
                      </button>
                    </div>
                  </Field>
                </div>

                <Field label="Estimated total ($)" required>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 text-sm">
                      $
                    </span>
                    <input
                      type="number"
                      min={0}
                      step={10}
                      value={total}
                      onChange={(e) => setTotal(e.target.value)}
                      placeholder="2480"
                      className="form-input pl-7"
                    />
                  </div>
                </Field>

                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={depositPaid}
                    onChange={(e) => setDepositPaid(e.target.checked)}
                    className="w-4 h-4 rounded border-ink-300 text-brand-500 cursor-pointer"
                  />
                  <span className="text-sm text-ink-700">
                    Deposit already collected from customer
                  </span>
                </label>

                {error && (
                  <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2 rounded-md">
                    <i className="ri-error-warning-line mt-0.5"></i>
                    <span>{error}</span>
                  </div>
                )}
              </div>
            )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-ink-100 bg-cream-50 flex items-center justify-between gap-2 shrink-0 rounded-b-lg">
            <button
              onClick={onClose}
              className="text-sm font-semibold text-ink-500 hover:text-ink-900 px-3 py-2 cursor-pointer"
            >
              Cancel
            </button>
            <div className="flex items-center gap-2">
              {step > 1 && (
                <button
                  onClick={() => setStep((s) => s - 1)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold border border-ink-200 text-ink-700 hover:bg-white cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back
                </button>
              )}
              {step < 3 ? (
                <button
                  onClick={() => setStep((s) => s + 1)}
                  disabled={step === 1 ? !canNext1 : !canNext2}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold bg-ink-900 hover:bg-ink-800 text-white cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Continue
                  <i className="ri-arrow-right-line"></i>
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold bg-brand-500 hover:bg-brand-600 text-white cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      {isEdit ? "Saving…" : "Creating…"}
                    </>
                  ) : (
                    <>
                      <i className="ri-check-line"></i>
                      {isEdit ? "Save Changes" : "Create Project"}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider text-ink-500 mb-1.5">
        {label}
        {required && <span className="text-rose-500 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}